import { Field, FieldProps } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { Form, FormControlProps } from 'react-bootstrap';
import { ErrorI18n } from '../error';

export type SelectProps = Omit<
  FormControlProps &
    React.SelectHTMLAttributes<HTMLSelectElement> & {
      controlId: string;
      options: SelectOptionType[];
      label?: string;
      className?: string;
      showValidation?: boolean;
      showError?: boolean;
      showEmptyOption?: boolean;
    },
  'type' | 'as' | 'value' | 'title' | 'onChange' | 'onBlur' | 'children'
>;

export type SelectOptionType = {
  label: string;
  value: string;
  orderIndex?: number;
  disabled?: boolean;
};

const orderList = (a: any, b: any) => {
  if (a.orderIndex == null && b.orderIndex == null) {
    // No sort when no OrderIndex
    return 0;
  }
  if (a.orderIndex === b.orderIndex && a.label) {
    return a.label.localeCompare(b.label);
  }
  return a.orderIndex - b.orderIndex;
};

export const Select = ({
  controlId,
  options,
  label,
  showValidation = true,
  showError = true,
  showEmptyOption = false,
  className = '',
  ...additionalAttributes
}: SelectProps) => {
  const computedOptions = useMemo(
    () =>
      [...options]
        .sort((a, b) => orderList(a, b))
        .map((option, index) => (
          <option value={option.value} key={index} disabled={option.disabled}>
            {option.label}
          </option>
        )),
    [options]
  );

  const selectedOption = useCallback((value: any) => options.filter((option) => option.value === value), [options]);

  return (
    <div className={`select${className ? ` ${className}` : ''}`}>
      <Field name={controlId}>
        {({ field, meta }: FieldProps) => (
          <Form.Group controlId={controlId} className="form-group">
            {label && <Form.Label>{label}</Form.Label>}
            <Form.Select
              value={field.value || ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              isValid={showValidation && !meta.error && meta.touched}
              isInvalid={showValidation && !!meta.error && meta.touched}
              title={selectedOption(field.value)[0]?.label || ''}
              {...additionalAttributes}
            >
              <option {...(showEmptyOption ? { hidden: false } : { hidden: true })} label="" />
              {computedOptions}
            </Form.Select>
            {showError && <ErrorI18n {...meta} />}
          </Form.Group>
        )}
      </Field>
    </div>
  );
};
